import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  Clock,
  MessageSquare,
  Mic,
  Save,
  ChevronDown,
  Check,
  Timer,
  Hourglass,
  Watch,
  Settings,
  Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { useAppSelector } from "../../hooks/redux/useRedux";
import {
  selectBookingTime,
  setUpdateToneOfVoice,
} from "../../redux/slices/profileSlice/ProfileSlice";
import { useAppDispatch } from "../../hooks/redux/useRedux";

const API = REST_API_URL;

export function BookingInformationCard() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [choices, setChoices] = useState(null);
  const [duration, setDuration] = useState("0");
  const [customDuration, setCustomDuration] = useState("");
  const [responseTime, setResponseTime] = useState("24 hours");
  const [openDropdown, setOpenDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const bookingTime = useAppSelector(selectBookingTime);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/service/booking-choices/`, {
          headers: buildHeaders(),
        });
        const data = await res.json();
        setChoices(data);
      } catch {
        toast.error(t("settings.failedToLoadChoices"));
      }
    })();
  }, [token, t]);

  const durationOptions = [
    {
      label: t("settings.none"),
      value: "0",
      icon: <Timer className="w-5 h-5" />,
    },
    {
      label: t("settings.15minutes"),
      value: "15",
      icon: <Clock className="w-5 h-5" />,
    },
    {
      label: t("settings.30minutes"),
      value: "30",
      icon: <Clock className="w-5 h-5" />,
    },
    {
      label: t("settings.1hour"),
      value: "60",
      icon: <Hourglass className="w-5 h-5" />,
    },
    {
      label: t("settings.2hours"),
      value: "120",
      icon: <Watch className="w-5 h-5" />,
    },
    {
      label: t("settings.custom"),
      value: "Other",
      icon: <Settings className="w-5 h-5" />,
    },
  ];

  const formatDuration = (m) => {
    m = +m;
    if (m === 0) return t("settings.none");
    const h = Math.floor(m / 60);
    const min = m % 60;
    if (h && !min)
      return `${h} ${t(h > 1 ? "settings.hours" : "settings.hour")}`;
    if (h && min) return `${h}${t("settings.h")} ${min}${t("settings.m")}`;
    return `${m} ${t("settings.min")}`;
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/service/booking-time/`, {
          headers: buildHeaders(),
        });
        const data = await res.json();

        if (data?.duration !== undefined) {
          const d = String(data.duration);
          if (durationOptions.some((o) => o.value === d)) {
            setDuration(d);
          } else {
            setDuration("Other");
            setCustomDuration(d);
          }
        }

        if (data?.response_time) {
          setResponseTime(data.response_time);
        }
      } catch {
        console.log("No saved booking-time settings yet.");
      }
    })();
  }, []);

  const save = async () => {
    const finalDuration =
      duration === "Other" ? Number(customDuration || 0) : Number(duration);

    const payload = {
      duration: finalDuration,
      time_unit: "minutes",
      response_time: responseTime,
      tone_of_voice_id: bookingTime?.tone_of_voice?.id,
    };

    setIsSaving(true);
    try {
      const res = await fetch(`${API}/service/booking-time/`, {
        method: "PUT",
        headers: buildHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error();

      toast.success(t("settings.settingsSaved"), {
        icon: "✅",
        style: {
          background: "#10B981",
          color: "white",
        },
      });
    } catch {
      toast.error(t("settings.errorSavingSettings"));
    } finally {
      setIsSaving(false);
    }
  };
  if (!choices)
    return (
      <div className="animate-pulse bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100">
        <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
      </div>
    );

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 bg-[#020637] rounded-xl shrink-0">
          <Clock className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg sm:text-xl font-normal text-gray-800">
            {t("settings.bookingSettings")}
          </h2>
        </div>
      </div>

      <div className="space-y-6 sm:space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#020637] shrink-0" />
              <h3 className="font-semibold text-gray-700 text-sm sm:text-base">
                {t("settings.bufferTime")}
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-gray-600">
              {t("settings.bufferTimeDescription")}
            </p>

            <div className="relative">
              <div className="relative">
                <button
                  onClick={() => setOpenDropdown(!openDropdown)}
                  className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl 
                           flex items-center justify-between hover:border-[#020637] 
                           transition-colors group"
                >
                  <div className="flex items-center gap-2.5 sm:gap-3">
                    <span className="text-[#020637] shrink-0">
                      {durationOptions.find((d) => d.value === duration)?.icon}
                    </span>
                    <span className="font-medium text-gray-700 text-sm sm:text-base text-left">
                      {duration === "Other"
                        ? customDuration
                          ? formatDuration(customDuration)
                          : t("settings.customDuration")
                        : formatDuration(duration)}
                    </span>
                  </div>
                  <motion.div
                    animate={{ rotate: openDropdown ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="shrink-0"
                  >
                    <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-[#020637]" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {openDropdown && (
                    <motion.div
                      className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 
                                rounded-xl shadow-lg z-10 overflow-hidden"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {durationOptions.map((option) => (
                        <motion.button
                          key={option.value}
                          whileHover={{ backgroundColor: "#f8fafc" }}
                          onClick={() => {
                            setDuration(option.value);
                            if (option.value !== "Other") {
                              setCustomDuration("");
                              setOpenDropdown(false);
                            }
                          }}
                          className={`w-full px-3.5 py-2.5 sm:px-4 sm:py-3 flex items-center justify-between 
                                   transition-colors ${
                                     duration === option.value
                                       ? "bg-[#020637]/5 text-[#020637]"
                                       : ""
                                   }`}
                        >
                          <div className="flex items-center gap-2.5 sm:gap-3">
                            <span
                              className={`shrink-0 ${
                                duration === option.value
                                  ? "text-[#020637]"
                                  : "text-gray-500"
                              }`}
                            >
                              {option.icon}
                            </span>
                            <span className="font-medium text-sm sm:text-base text-left">
                              {option.label}
                            </span>
                          </div>
                          {duration === option.value && (
                            <Check className="w-4 h-4 text-[#020637] shrink-0" />
                          )}
                        </motion.button>
                      ))}

                      {duration === "Other" && (
                        <div className="p-3 sm:p-4 border-t border-gray-100">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                            <input
                              type="number"
                              min="0"
                              placeholder={t("settings.enterMinutes")}
                              value={customDuration}
                              onChange={(e) =>
                                setCustomDuration(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && customDuration) {
                                  setOpenDropdown(false);
                                }
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                                       focus:ring-2 focus:ring-[#020637]/15 focus:border-[#020637] outline-none"
                            />
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#020637] shrink-0" />
              <h3 className="font-semibold text-gray-700 text-sm sm:text-base">
                {t("settings.responseTime")}
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-gray-600">
              {t("settings.responseTimeDescription")}
            </p>

            <div className="flex gap-2.5 sm:gap-3">
              {choices.response_time_choices?.map((item, i) => (
                <motion.label
                  key={i}
                  whileHover={{ scale: 1.01 }}
                  className={`flex items-center justify-between p-2 sm:p-3 rounded-xl border cursor-pointer 
                           transition-all gap-2 ${
                             responseTime === item.value
                               ? "border-[#020637] bg-[#020637]/5"
                               : "border-gray-200 hover:border-gray-300"
                           }`}
                >
                  <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                    <MessageSquare className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="font-medium text-gray-700 text-sm sm:text-base">
                      {/* {item.value} */}
                      {t(`settings.${item.value}`)}
                    </span>
                  </div>
                  <div className="relative shrink-0">
                    <input
                      type="radio"
                      name="responseTime"
                      checked={responseTime === item.value}
                      onChange={() => setResponseTime(item.value)}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                                  ${
                                    responseTime === item.value
                                      ? "border-[#020637] bg-[#020637]"
                                      : "border-gray-300"
                                  }`}
                    >
                      {responseTime === item.value && (
                        <div className="w-2 h-2 bg-white rounded-full" />
                      )}
                    </div>
                  </div>
                </motion.label>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <Mic className="w-5 h-5 text-[#020637] shrink-0" />
            <h3 className="font-semibold text-gray-700 text-sm sm:text-base">
              {t("settings.toneOfVoice")}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {choices.tone_of_voice_options?.map((tone) => (
              <motion.div
                key={tone.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => dispatch(setUpdateToneOfVoice(tone.id))}
                className={`cursor-pointer rounded-xl border-2 p-4 sm:p-5 transition-all
           ${
             bookingTime?.tone_of_voice?.id === tone.id
               ? "border-[#020637] bg-[#020637]/5"
               : "border-gray-200 hover:border-gray-300"
           }`}
              >
                <div className="flex items-start justify-between mb-2.5 sm:mb-3 gap-2">
                  <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
                    <div
                      className={`p-2 rounded-lg shrink-0 ${
                        bookingTime?.tone_of_voice?.id === tone.id
                          ? "bg-[#020637]/10"
                          : "bg-gray-100"
                      }`}
                    >
                      <MessageSquare
                        className={`w-4 h-4 ${
                          bookingTime?.tone_of_voice?.id === tone.id
                            ? "text-[#020637]"
                            : "text-gray-600"
                        }`}
                      />
                    </div>
                    <h4
                      className="font-semibold text-gray-800 text-sm sm:text-base truncate"
                      title={tone.title}
                    >
                      {tone.title}
                    </h4>
                  </div>
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0
                                ${
                                  bookingTime?.tone_of_voice?.id === tone.id
                                    ? "bg-[#020637]"
                                    : "bg-gray-100"
                                }`}
                  >
                    {bookingTime?.tone_of_voice?.id === tone.id && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                  {tone.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.button
          onClick={save}
          disabled={isSaving}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full md:w-auto px-6 py-2.5 sm:px-8 sm:py-3 bg-[#020637] hover:bg-[#0e1c7a] 
                   text-white font-semibold rounded-xl shadow-md hover:shadow-lg 
                   transition-all flex items-center justify-center gap-3
                   disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
        >
          {isSaving ? (
            <>
              <div
                className="w-4 h-4 border-2 border-white border-t-transparent
                            rounded-full animate-spin shrink-0"
              />
              {t("settings.saving")}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 shrink-0" />
              {t("settings.saveSettings")}
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
}
